import React, { useState, useRef, useEffect } from "react";
import { ListVideo, Copy, Check, ChevronDown, ChevronUp } from "lucide-react";

// No video is recorded by this app — a chapter's "jump to" scrolls the
// stored transcript to that line instead of seeking a video player.
// Deep links (?meetingId=&chapter=) drive initialChapterIndex from Meetings.jsx.
const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function MeetingChapters({ meetingId, chapters, transcript, T, initialChapterIndex }) {
  const [expanded, setExpanded] = useState(initialChapterIndex != null);
  const [activeIndex, setActiveIndex] = useState(initialChapterIndex ?? null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const lineRefs = useRef({});

  const jumpTo = (chapter) => {
    setExpanded(true);
    setActiveIndex(chapter.index);
    requestAnimationFrame(() => {
      lineRefs.current[chapter.start_line]?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  useEffect(() => {
    if (initialChapterIndex == null || !chapters?.length) return;
    const ch = chapters.find((c) => c.index === initialChapterIndex);
    if (ch) jumpTo(ch);
    // jumpTo is stable for a given chapters/meetingId prop set; only re-run on index change
  }, [initialChapterIndex, chapters]);

  const copyChapterLink = (chapter) => {
    const url = new URL(window.location.href);
    url.searchParams.set("meetingId", meetingId);
    url.searchParams.set("chapter", chapter.index);
    navigator.clipboard.writeText(url.toString()).then(() => {
      setCopiedIdx(chapter.index);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  if (!chapters?.length) return null;

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: T.text }}>
          <ListVideo size={13} style={{ color: T.cyan }} /> Chapters
        </div>
        <button onClick={() => setExpanded((e) => !e)}
          style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
          {expanded ? <>Hide transcript <ChevronUp size={12} /></> : <>Show transcript <ChevronDown size={12} /></>}
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: expanded ? 12 : 0 }}>
        {chapters.map((c) => (
          <div key={c.index} onClick={() => jumpTo(c)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer",
              background: activeIndex === c.index ? `${T.cyan}14` : T.card2,
              border: `1px solid ${activeIndex === c.index ? `${T.cyan}55` : T.border}` }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: T.cyan, minWidth: 52 }}>{fmtTime(c.start_time)}</span>
            <span style={{ fontSize: 12.5, color: T.text, fontWeight: 600, flex: 1 }}>{c.title}</span>
            <button onClick={(e) => { e.stopPropagation(); copyChapterLink(c); }} title="Copy link to this chapter"
              style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", alignItems: "center" }}>
              {copiedIdx === c.index ? <Check size={12} style={{ color: T.green }} /> : <Copy size={12} />}
            </button>
          </div>
        ))}
      </div>

      {expanded && (
        <div style={{ maxHeight: 260, overflowY: "auto", background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px" }}>
          {(transcript || []).map((line, i) => {
            const isChapterStart = chapters.some((c) => c.start_line === i);
            return (
              <div key={i} ref={(el) => (lineRefs.current[i] = el)}
                style={{ padding: "5px 4px", borderRadius: 6, scrollMarginTop: 8,
                  borderTop: isChapterStart && i !== 0 ? `1px dashed ${T.border}` : "none",
                  marginTop: isChapterStart && i !== 0 ? 6 : 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.cyan }}>{line.speaker}: </span>
                <span style={{ fontSize: 12, color: T.text }}>{line.text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
